import React, { useState, useEffect } from "react";
import { Card, Button, Badge } from "react-bootstrap";
import API from "../../services/api";
import SlotSelectionModal from "./SlotSelectionModal";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";
const DEFAULT_IMAGE = "/images/default-placeholder.jpg";
const BORDER_IMAGE = "cardbg.jpeg";

const MenuCard = ({ type, item, handleAddToCart, getCategoryName, slotData, cart}) => {
    const [showSlotModal, setShowSlotModal] = useState(false);
    const [activeSlot, setActiveSlot] = useState(null);
    const [selectedFoods, setSelectedFoods] = useState([]);
    const [mergedSlotData, setMergedSlotData] = useState(slotData);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [showSpiceModal, setShowSpiceModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [ln, setLn] = useState("jp");

    /* ---------------------- NEW: ERROR / TOAST SYSTEM ----------------------- */
    const [errorMessage, setErrorMessage] = useState("");
    const [showError, setShowError] = useState(false);

    // Add at the top of MenuCard function
    const isInCart = (item, type) => {
        return cart.some(c => {
            if (type === "food") return c.type === "food" && c.title === item.name;
            if (type === "set_menu") return c.type === "set_menu" && c.title === item.title;
            return false;
        });
    };

    const inCart = isInCart(item, type);

    const triggerError = (msg) => {
        setErrorMessage(msg);
        setShowError(true);
        setTimeout(() => setShowError(false), 2500);
    };
    /* ------------------------------------------------------------------------ */

    const SPICE_LEVELS = [
        { value: "mild", label: "1 マイルド (MILD)" },
        { value: "medium", label: "2 普通 (MEDIUM)" },
        { value: "hot", label: "3 中辛 (HOT)" },
        { value: "very_hot", label: "4 大辛 (VERY HOT)" },
        { value: "super_hot", label: "5 激辛 (SUPER HOT)" },
    ];

    const SPICE_LEVELS_MAPPER = [
        { value: "mild", label: "マイルド" },
        { value: "medium", label: "普通" },
        { value: "hot", label: "中辛" },
        { value: "very_hot", label: "大辛" },
        { value: "super_hot", label: "激辛" },
    ];

    const renderSpiceChili = (level) => {
        const index = SPICE_LEVELS.findIndex((l) => l.value === level);
        if (index === -1) return null;
        if (index == 0) return '';
        return "🌶".repeat(index + 1);
    };

    const renderSpiceChiliCard = (level) => {
        const index = SPICE_LEVELS_MAPPER.findIndex((l) => l.value === level);
        if (index === -1) return null;
        return SPICE_LEVELS_MAPPER[index].label ?? '';
    };

    const handleSpiceButtonClick = (slotOrItem) => {
        if (type === "food") {
            setSelectedItem(slotOrItem);
        } else {
            setSelectedSlot(slotOrItem);
        }
        setShowSpiceModal(true);
    };

    const handleSpiceSelect = (value) => {
        if (type === "food" && selectedItem) {
            selectedItem.spice_level = value;
        } else if (selectedSlot) {
            selectedSlot.spice_level = value;
        }
        setShowSpiceModal(false);
    };

    useEffect(() => {
        if (type === "set_menu" && item) {
            const fetchChoosableFoods = async () => {
                const foods = await getChoosableFoodsByCategory(item);
                const newData = mergeArraysById(slotData, foods);
                setMergedSlotData(newData);
            };
            fetchChoosableFoods();
        }
    }, [item, type, slotData]);

    function mergeArraysById(originalArray, newArray) {
        const dedupeFoods = (array) =>
            array.map((slot) => {
                if (!Array.isArray(slot.foods)) return slot;
                const uniqueFoodsMap = new Map();
                slot.foods.forEach((f) => uniqueFoodsMap.set(f.id, f));
                return { ...slot, foods: Array.from(uniqueFoodsMap.values()) };
            });

        const uniqueOriginal = dedupeFoods(originalArray);
        const uniqueNew = dedupeFoods(newArray);

        return uniqueOriginal.map((item) => {
            const match = uniqueNew.find((n) => n.id === item.id);
            return match ? { ...item, ...match } : item;
        });
    }

    const getChoosableFoodsByCategory = async (item) => {
        if (!item?.slots) return [];
        const choosableCategoryIds = item.slots
            .filter((slot) => slot.type === "choosable")
            .map((slot) => slot.category_id);

        if (choosableCategoryIds.length === 0) return [];

        try {
            const responses = await Promise.all(
                choosableCategoryIds.map((id) =>
                    API.get(`/admin/categories/${id}/foods`)
                )
            );
            return responses.map((res, index) => ({
                id: choosableCategoryIds[index],
                foods: res.data || [],
            }));
        } catch (err) {
            console.error("Failed to fetch foods for choosable categories", err);
            return [];
        }
    };

    const imageSrc =
        type === "food"
            ? item.images?.[0]
                ? BASE_URL + "/storage/" + item.images[0]
                : DEFAULT_IMAGE
            : item.image
                ? BASE_URL + "/storage/" + item.image
                : DEFAULT_IMAGE;

    const price = type === "food" ? item.price : item.base_price || 0;

    const handleSlotButtonClick = (slot) => {
        setActiveSlot(slot);

        // Clear any previous error when opening slot
        if (slot._error) {
            slot._error = false;
            setShowError(false);
        }

        setSelectedFoods(
            slot.type === "choosable"
                ? [...(slot.selected_foods || [])].filter(Boolean)
                : [slot.default_food].filter(Boolean)
        );

        setShowSlotModal(true);
    };

    const buildCartData = (item) => {
        const updatedSlots = item.slots.map((slot) => {
            if (slot.type === "choosable") {
                return { ...slot, chosen_foods: slot.selected_foods || [] };
            } else if (slot.type === "changeable") {
                return { ...slot, chosen_food: slot.default_food };
            }
            return slot;
        });
        return { ...item, slots: updatedSlots };
    };

    const handleFoodSelect = (food) => {
        if (!activeSlot) return;

        // Clear error for this slot when user selects something
        if (activeSlot._error) {
            activeSlot._error = false;
            setShowError(false);
        }

        if (activeSlot.type === "choosable") {
            const alreadySelected = selectedFoods.find((f) => f.id === food.id);
            let updated;
            if (alreadySelected) {
                updated = selectedFoods.filter((f) => f.id !== food.id);
            } else {
                if (selectedFoods.length < activeSlot.quantity) {
                    updated = [...selectedFoods, food];
                } else return;
            }
            setSelectedFoods(updated);
            activeSlot.selected_foods = updated;
        } else {
            setSelectedFoods([food]);
            activeSlot.default_food = food;
            activeSlot.additional_price = food.additional_price || 0;
        }
    };

    const handleConfirmSlot = () => {
        setShowSlotModal(false);
    };

    /* ======================= NEW VALIDATION FOR SET MENU ====================== */
    const validateSlotsBeforeAdd = () => {
        let missing = [];

        item.slots.forEach((slot) => {
            slot._error = false;

            if (slot.type === "choosable") {
                const selectedCount = slot.selected_foods?.length || 0;

                // MUST select exactly the required quantity
                if (selectedCount !== slot.quantity) {
                    slot._error = true;

                    missing.push(
                        `${getCategoryName(slot.category_id)} (Select ${slot.quantity})`
                    );
                }
            }

            if (slot.type === "changeable" && !slot.default_food) {
                slot._error = true;
                missing.push(getCategoryName(slot.category_id));
            }
        });

        if (missing.length > 0) {
            triggerError(`Please select required items: ${missing.join(", ")}`);
            return false;
        }

        return true;
    };
    /* =========================================================================== */

    return (
        <>
            <div style={{ display: "flex", flexDirection: "column", verticalAlign: "top" }}>
                <div style={{ width: "1155px", height: "530px", cursor: "pointer", maxWidth: "77vw", minWidth: "77vh", boxSizing: "border-box" }}>
                    <Card className="d-flex flex-column shadow-sm" style={{ flex: 1, width: "98%", height: "100%", backgroundColor: "#1e1e1e", color: "#fff", overflow: "hidden", border: "none", position: "relative" }}>
                        <Card.Img
                            src={imageSrc}
                            alt={item.title}
                            onError={(e) => (e.target.src = DEFAULT_IMAGE)}
                            style={{ width: "100%", height: "100%" }}
                        />
                    </Card>
                </div>

                <div style={{ width: "1155px", cursor: "pointer", maxWidth: "77vw", minWidth: "77vh", boxSizing: "border-box", height: "150vh" }}>
                    <Card className="d-flex flex-column shadow-sm" style={{ width: "98%", height: "50%", backgroundColor: "#f15a22", color: "#fff", overflow: "hidden", border: "none" }}>
                        <Card.ImgOverlay className="d-flex flex-column p-3" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0) 100%)" }}>

                            {/* FOOD TYPE */}
                            {type === "food" && (
                                <>
                                    <div className="text-center mb-2">
                                        <Card.Title style={{ fontWeight: 700, fontSize: "3.6rem" }}>¥{parseFloat(price || 0).toFixed(0)}</Card.Title>
                                        <Card.Title style={{ fontWeight: 800, fontSize: "1.5rem" }}>
                                            {item.japanese_name}
                                            {item.spice_level && (
                                                <Button
                                                    size="lg"
                                                    variant="outline-light"
                                                    className="mx-2"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleSpiceButtonClick(item);
                                                    }}
                                                >
                                                    {renderSpiceChili(item.spice_level)}{" "}
                                                    {SPICE_LEVELS.find((l) => l.value === item.spice_level)?.label || item.spice_level}
                                                </Button>
                                            )}
                                        </Card.Title>

                                        <Card.Title style={{ fontWeight: 500, fontSize: "1.3rem" }}>{item.title}</Card.Title>
                                        <Card.Text style={{ fontWeight: 700, fontSize: "1rem" }}>{item.jp_description}</Card.Text>
                                    </div>

                                    <Button
                                        style={{ padding: "15px", fontSize: "25px", fontWeight: 500 }}
                                        variant={inCart ? "success" : "light"} // green if added
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleAddToCart(item, type);
                                        }}
                                    >
                                        {inCart? "Add to Cart - カートに追加 (追加した)" : "Add to Cart - カートに追加"}
                                    </Button>
                                </>
                            )}

                            {/* SET MENU TYPE */}
                            {type === "set_menu" && (
                                <>
                                    <div className="mb-2 mx-auto text-center">
                                        <Card.Title style={{
                                            fontWeight: 700,
                                            fontSize: "3.6rem"
                                        }}>¥{parseFloat(price).toFixed(0)}</Card.Title>
                                        <Card.Title style={{
                                            fontWeight: 800,
                                            fontSize: "1.5rem"
                                        }}>{item.japanese_title}</Card.Title>
                                        {/*<Card.Title*/}
                                        {/*    style={{fontWeight: 500, fontSize: "1.3rem"}}>{item.title}</Card.Title>*/}

                                        <div style={{display: "flex", justifyContent: "center"}}>
                                            <Card.Text style={{
                                                display: "flex",
                                                justifyContent: "center",
                                                fontWeight: 700,
                                                fontSize: "1.3rem",
                                                maxWidth: "70ch",
                                                textOverflow: "unset",
                                                whiteSpace: "normal",
                                                overflow: "hidden"
                                            }}>
                                                {item.jp_description}
                                            </Card.Text>
                                        </div>

                                        <div style={{
                                            display: "flex",
                                            flexDirection: "row",
                                            gap: "12px",
                                            overflowX: "auto",
                                            padding: "5px 0",
                                            justifyContent: "center",
                                        }}>
                                            {item.slots
                                                ?.slice()
                                                .sort((a, b) => {
                                                    const order = {fixed: 0, choosable: 1, changeable: 2};
                                                    return (order[a.type] ?? 3) - (order[b.type] ?? 3);
                                                })
                                                .map((slot, idx) => (
                                                    <div
                                                        key={idx}
                                                        style={{
                                                            width: "max-content",
                                                            // borderRadius: "8px",
                                                            // background: "linear-gradient(135deg, #ff7e5f, #feb47b)",
                                                            padding: "8px",
                                                            border: slot._error ? "2px solid red" : ""
                                                        }}
                                                    >
                                                        {/* 🔥 Your EXISTING slot content stays EXACTLY the same: */}

                                                        <p
                                                            style={{
                                                                fontWeight: 700,
                                                                fontSize: slot.type === "fixed" ? "1.3rem" : "2rem",
                                                                paddingTop: slot.type === "fixed" ? "1.3rem" : "inherit"
                                                            }}
                                                        >
                                                            {getCategoryName(slot.category_id, true)}:
                                                        </p>

                                                        {(slot.type === "choosable" || slot.type === "changeable") && (
                                                            <Button
                                                                size="lg"
                                                                style={{
                                                                    padding: "20px 40px",
                                                                    fontSize: "1.50rem" // or "large" depending on your framework
                                                                }}
                                                                variant={slot.type === "changeable" ? "warning" : "info"}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleSlotButtonClick(slot);
                                                                }}
                                                            >
                                                                {slot.type === "changeable" ? "変化" : "選ぶ"}
                                                            </Button>
                                                        )}

                                                        {/* Spice buttons */}
                                                        {slot.type === "fixed" && slot.default_food?.spice_level && (
                                                            <Button
                                                                size="lg"
                                                                variant="outline-light"
                                                                className="mx-2"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleSpiceButtonClick(slot);
                                                                }}
                                                            >
                                                                {renderSpiceChili(slot.default_food.spice_level)}{" "}
                                                                {SPICE_LEVELS.find((l) => l.value === slot.default_food.spice_level)?.label}
                                                            </Button>
                                                        )}

                                                        {slot.type === "changeable" && slot.default_food?.spice_level && (
                                                            <Button
                                                                size="lg"
                                                                variant="outline-light"
                                                                className="mx-2"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleSpiceButtonClick(slot);
                                                                }}
                                                            >
                                                                {renderSpiceChili(slot.default_food.spice_level)}{" "}
                                                                {SPICE_LEVELS.find((l) => l.value === slot.default_food.spice_level)?.label}
                                                            </Button>
                                                        )}

                                                        {/* Choosable selected foods */}
                                                        {slot.type === "choosable" ? (
                                                            // ✅ IF choosable → show selected foods
                                                            slot.selected_foods?.map((f) => (
                                                                <div key={f.id} style={{ fontSize: ".9rem", fontWeight: "bold" }}>
                                                                    {/*{f.name}*/}
                                                                    {f.japanese_name}
                                                                    {f.spice_level && renderSpiceChiliCard(f.spice_level)}
                                                                </div>
                                                            ))
                                                        ) : (
                                                            // ✅ ELSE → show default food
                                                            <div
                                                                className="me-2"
                                                                style={{
                                                                    fontSize: ".9rem",
                                                                    fontWeight: "bold",
                                                                }}
                                                            >
                                                                {slot.default_food.japanese_name}
                                                            </div>
                                                        )}

                                                        {/* Error */}
                                                        {slot._error && (
                                                            <div style={{
                                                                color: "red",
                                                                fontSize: "0.9rem",
                                                                marginTop: "4px"
                                                            }}>
                                                                Please select an item for this category
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                        </div>
                                    </div>

                                    {/* ADD SET MENU BUTTON WITH VALIDATION */}
                                    <Button
                                        style={{padding: "15px", fontSize: "25px", fontWeight: 500}}
                                        variant={inCart? "success" : "light"} // green if added
                                        onClick={(e) => {
                                            e.stopPropagation();

                                            if (!validateSlotsBeforeAdd()) return;

                                            const cartItem = buildCartData(item);
                                            handleAddToCart(cartItem, type);
                                        }}
                                    >
                                        {inCart ? "Add to Cart - カートに追加 (追加した)" : "Add to Cart - カートに追加"}
                                    </Button>
                                </>
                            )}
                        </Card.ImgOverlay>
                    </Card>
                </div>
            </div>

            <SlotSelectionModal
                show={showSlotModal}
                onHide={() => setShowSlotModal(false)}
                activeSlot={activeSlot}
                slotData={mergedSlotData}
                selectedFoods={selectedFoods}
                handleFoodSelect={handleFoodSelect}
                handleConfirmSlot={handleConfirmSlot}
            />

            {/* SPICE MODAL */}
            <div
                className={`modal fade ${showSpiceModal ? "show d-block" : ""}`}
                tabIndex="-1"
                role="dialog"
                style={{
                    backgroundColor: showSpiceModal ? "rgba(0,0,0,0.5)" : "transparent",
                }}
            >
                <div className="modal-dialog" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Select Spice Level</h5>
                            <button type="button" className="close" onClick={() => setShowSpiceModal(false)}>
                                <span>&times;</span>
                            </button>
                        </div>

                        <div className="modal-body">
                            {SPICE_LEVELS.map((level) => (
                                <button
                                    key={level.value}
                                    type="button"
                                    className={`btn btn-outline-primary btn-block mb-2 ${
                                        selectedSlot?.spice_level === level.value ||
                                        selectedItem?.spice_level === level.value
                                            ? "active"
                                            : ""
                                    }`}
                                    onClick={() => handleSpiceSelect(level.value)}
                                >
                                    {level.label}
                                </button>
                            ))}
                        </div>

                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary"
                                    onClick={() => setShowSpiceModal(false)}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* NEW TOAST MESSAGE */}
            {showError && (
                <div
                    style={{
                        position: "fixed",
                        bottom: "30px",
                        right: "30px",
                        background: "rgba(220,53,69,0.9)",
                        padding: "15px 20px",
                        color: "white",
                        borderRadius: "8px",
                        fontSize: "1.1rem",
                        zIndex: 9999,
                        boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                    }}
                >
                    {errorMessage}
                </div>
            )}
        </>
    );
};

export default MenuCard;
